import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Table,
  Card,
  Button,
  Select,
  DatePicker,
  Space,
  Tag,
  Typography,
  Spin,
  Row,
  Col,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  TeamOutlined,
  EyeOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../store/auth';
import { getTasks, getAssignableUsers } from '../lib/api';
import { getUserDisplayName } from '../lib/user';
import type { ColumnType } from 'antd/es/table';

const { Title } = Typography;

interface Assignee {
  userId: number;
  status: 'unread' | 'read' | 'in_progress' | 'completed' | null;
}

interface Task {
  id: number;
  title: string;
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'in_progress' | 'completed';
  dueDate: string | null;
  assignees?: Assignee[];
  groups?: { id: number; name: string }[];
}
const { Option } = Select;
const { RangePicker } = DatePicker;

export default function TasksPage() {
  const { user } = useAuthStore();
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [assignableUsers, setAssignableUsers] = useState<any[]>([]);

  // Filters
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('');
  const [filterDateRange, setFilterDateRange] = useState<[string, string] | null>(null);
  const [filterAssignee, setFilterAssignee] = useState<string>('');

  useEffect(() => {
    if (user?.role === 'boss' || user?.role === 'manager') {
      getAssignableUsers().then(setAssignableUsers).catch(console.error);
    }
  }, [user]);

  useEffect(() => {
    loadTasks();
  }, [filterStatus, filterPriority, filterDateRange, filterAssignee]);

  const loadTasks = async () => {
    try {
      const filters: {
        status?: string;
        priority?: string;
        dueDate?: string;
        dueDateEnd?: string;
        assigneeId?: number;
      } = {};

      if (filterStatus !== 'all') filters.status = filterStatus;
      if (filterPriority) filters.priority = filterPriority;
      if (filterDateRange) {
        filters.dueDate = filterDateRange[0];
        filters.dueDateEnd = filterDateRange[1];
      }
      if (filterAssignee) filters.assigneeId = parseInt(filterAssignee);

      const data = await getTasks(filters);
      setTasks(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFilterStatus('all');
    setFilterPriority('');
    setFilterDateRange(null);
    setFilterAssignee('');
  };

  const getPriorityTag = (priority: string) => {
    const config: Record<string, { color: string; label: string }> = {
      high: { color: 'red', label: '高' },
      medium: { color: 'orange', label: '中' },
      low: { color: 'green', label: '低' },
    };
    const { color, label } = config[priority] || config.low;
    return <Tag color={color}>{label}</Tag>;
  };

  const getStatusTag = (status: string) => {
    const config: Record<string, { color: string; label: string }> = {
      pending: { color: 'default', label: '待處理' },
      in_progress: { color: 'processing', label: '進行中' },
      completed: { color: 'success', label: '已完成' },
    };
    const { color, label } = config[status] || config.pending;
    return <Tag color={color}>{label}</Tag>;
  };

  const getPersonalStatusTag = (status: string | null) => {
    if (!status) return null;
    const config: Record<string, { color: string; label: string }> = {
      unread: { color: 'default', label: '未讀' },
      read: { color: 'blue', label: '已讀' },
      in_progress: { color: 'processing', label: '進行中' },
      completed: { color: 'success', label: '已完成' },
    };
    const { color, label } = config[status] || config.unread;
    return <Tag color={color}>{label}</Tag>;
  };

  const renderAssignees = (task: any) => {
    const groups = task.groups || [];
    const assignees = task.assignees || [];
    const allAssignees = [
      ...groups.map((g: any) => ({ ...g, isGroup: true })),
      ...assignees.map((a: any) => ({ ...a, isGroup: false })),
    ];

    if (allAssignees.length <= 3) {
      return (
        <Space size="small" wrap>
          {allAssignees.map((item, idx) => (
            <Tag key={idx} color={item.isGroup ? 'purple' : 'blue'}>
              {item.isGroup ? item.name : getUserDisplayName(item)}
            </Tag>
          ))}
        </Space>
      );
    }

    const visible = allAssignees.slice(0, 2);
    const remaining = allAssignees.slice(2);

    return (
      <Space size="small" wrap>
        {visible.map((item, idx) => (
          <Tag key={idx} color={item.isGroup ? 'purple' : 'blue'}>
            {item.isGroup ? item.name : getUserDisplayName(item)}
          </Tag>
        ))}
        <Tooltip
          title={
            <Space orientation="vertical" size="small">
              {remaining.map((item, idx) => (
                <span key={idx}>
                  {item.isGroup ? item.name : getUserDisplayName(item)}
                </span>
              ))}
            </Space>
          }
        >
          <Tag>+{remaining.length}</Tag>
        </Tooltip>
      </Space>
    );
  };

  const employeeColumn: ColumnType<Task> = {
    title: '我的狀態',
    key: 'myStatus',
    width: 100,
    render: (_, record) => {
      const myAssignment = record.assignees?.find(
        (a) => a.userId === user?.id
      );
      return getPersonalStatusTag(myAssignment?.status || null);
    },
  };

  const columns: ColumnType<Task>[] = [
    {
      title: '任務名稱',
      dataIndex: 'title',
      key: 'title',
      fixed: 'left',
      width: 160,
      render: (title) => <span style={{ fontWeight: 500 }}>{title}</span>,
    },
    {
      title: '被指派人',
      key: 'assignees',
      responsive: ['md'],
      render: (_, record) => renderAssignees(record),
    },
    {
      title: '優先級',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      responsive: ['sm'],
      render: (priority) => getPriorityTag(priority),
    },
    {
      title: '整體狀態',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => getStatusTag(status),
    },
    ...(user?.role === 'employee' ? [employeeColumn] : []),
    {
      title: '截止日期',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 110,
      responsive: ['lg'],
      render: (dueDate) =>
        dueDate ? new Date(dueDate).toLocaleDateString('zh-TW') : '-',
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 80,
      render: (_, record) => (
        <Link to={`/tasks/${record.id}`}>
          <Button type="link" icon={<EyeOutlined />} size="small">
            查看
          </Button>
        </Link>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size="large">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          任務列表
        </Title>
        <Space>
          {(user?.role === 'boss' || user?.role === 'manager') && (
            <>
              <Link to="/groups">
                <Button icon={<TeamOutlined />}>群組管理</Button>
              </Link>
              <Link to="/tasks/new">
                <Button type="primary" icon={<PlusOutlined />}>
                  指派新任務
                </Button>
              </Link>
            </>
          )}
        </Space>
      </div>

      <Card>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={12} md={6} lg={5}>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                狀態
              </label>
              <Select
                style={{ width: '100%' }}
                value={filterStatus}
                onChange={setFilterStatus}
                placeholder="全部狀態"
              >
                <Option value="all">全部狀態</Option>
                <Option value="pending">待處理</Option>
                <Option value="in_progress">進行中</Option>
                <Option value="completed">已完成</Option>
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={12} md={6} lg={5}>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                優先級
              </label>
              <Select
                style={{ width: '100%' }}
                value={filterPriority}
                onChange={setFilterPriority}
                placeholder="全部優先級"
                allowClear
              >
                <Option value="high">高</Option>
                <Option value="medium">中</Option>
                <Option value="low">低</Option>
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={12} md={6} lg={7}>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                截止日期範圍
              </label>
              <RangePicker
                style={{ width: '100%' }}
                value={
                  filterDateRange
                    ? [dayjs(filterDateRange[0]), dayjs(filterDateRange[1])] as any
                    : null
                }
                onChange={(dates: any) => {
                  if (dates) {
                    setFilterDateRange([dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')]);
                  } else {
                    setFilterDateRange(null);
                  }
                }}
              />
            </div>
          </Col>

          {(user?.role === 'boss' || user?.role === 'manager') && (
            <Col xs={24} sm={12} md={6} lg={5}>
              <div>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  被指派人
                </label>
                <Select
                  style={{ width: '100%' }}
                  value={filterAssignee}
                  onChange={setFilterAssignee}
                  placeholder="全部人員"
                  allowClear
                >
                  {assignableUsers.map((u) => (
                    <Option key={u.id} value={u.id}>
                      {getUserDisplayName(u)}
                    </Option>
                  ))}
                </Select>
              </div>
            </Col>
          )}

          <Col xs={24} sm={12} md={6} lg={2}>
            <div style={{ marginTop: 28 }}>
              <Button icon={<ClearOutlined />} onClick={handleClearFilters} block>
                清除
              </Button>
            </div>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          dataSource={tasks}
          columns={columns}
          rowKey="id"
          scroll={{ x: 'max-content' }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 筆`,
          }}
          locale={{
            emptyText: '目前沒有符合條件的任務',
          }}
        />
      </Card>
    </Space>
  );
}
